import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { adminSectionLibraryService } from '../../services/admin.sectionLibrary.service'
import type { ApplicationSection } from '@/types'

const schema = z.object({
  sectionId: z
    .string()
    .min(1, 'Section ID is required')
    .regex(/^[a-z0-9-]+$/, 'Section ID may only contain lowercase letters, numbers, and hyphens'),
  category: z.string(),
})

type FormValues = z.infer<typeof schema>

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  section: ApplicationSection | null
  onSaved: () => void
}

export function SaveSectionToLibraryDialog({ open, onOpenChange, section, onSaved }: Props) {
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      sectionId: section ? slugify(section.title) : '',
      category: 'General',
    },
  })

  async function onSubmit(values: FormValues) {
    if (!section) return
    setError(null)
    try {
      await adminSectionLibraryService.create({
        sectionId: values.sectionId,
        title: section.title,
        description: section.description ?? '',
        category: values.category,
        fields: section.fields,
      })
      reset()
      onSaved()
      onOpenChange(false)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to save to library'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save Section to Library</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="sectionId">Section ID</Label>
            <Input
              id="sectionId"
              className="font-mono"
              placeholder="applicant-info"
              {...register('sectionId')}
            />
            <p className="text-xs text-muted-foreground">
              Unique identifier for this template. Cannot be changed once set.
            </p>
            {errors.sectionId && (
              <p className="text-sm text-destructive">{withErrorCode(String(errors.sectionId.message), 'app/validation')}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              placeholder="General"
              {...register('category')}
            />
          </div>

          {section && (
            <p className="text-xs text-muted-foreground">
              Saving <span className="font-medium text-foreground">{section.fields.length}</span>{' '}
              field{section.fields.length === 1 ? '' : 's'} with this section.
            </p>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save to Library'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
